import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import {
  computeChangedFields,
  Destination,
  getDefaultAuthMethod,
  getDefaultBucketVendor,
  getDefaultMetastore,
  getDefaultStorage,
  getDefaultStorageAuthMethod,
  PreparedDestination,
  prepareDestinationFromExisting,
  prepareDestinationWithForm,
  useDestination as useDestinationSDK,
  useDestinationForm,
} from "@prequel/react";
import { Alert, AlertKind, Button, ButtonStyle, DocumentTextIcon, Spinner } from "@prequel/shared-components";

import ConfirmDataDestinationModal from "../../../components/ConfirmDataDestinationModal";
import EgressIPsInstructions from "../../../components/EgressIPsInstructions";
import TestDestinationConnection from "../TestDestinationConnection";
import AdvancedOptionsSection from "./AdvancedOptionsSection";
import DocumentationSidebar from "./DocumentationSidebar";
import { canSaveWithoutTest, initialFormState } from "./helpers";
import Models from "./Models";

import { useCreateDestination, useDestination, useUpdateDestination } from "@/api/queries/destinations";
import { useCreateDestinationDocs } from "@/api/queries/docs";
import { useOrg } from "@/api/queries/orgs";
import { useAllRecipients } from "@/api/queries/recipients";
import WrapperSelector from "@/components/WrapperSelector";
import { env } from "@/env";
import { extractDataDestination } from "@/types";

const RECIPIENT_SECTION_ID = 2;
const ADVANCED_OPTIONS_SECTION_ID = 6;

// This file fails exhaustive-deps linting. Auto-fixing it breaks logic, and this needs a broader refactor.
const DestinationForm = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const { destinationID } = useParams<{ destinationID: string | undefined }>();
  const [destination, setDestination] = useDestinationSDK();
  const destinationQueryResult = useDestination(destinationID);
  const { data: recipients } = useAllRecipients();
  const { data: org } = useOrg();
  const createDestinationMutation = useCreateDestination();
  const updateDestinationMutation = useUpdateDestination();

  const destinationToEdit = destinationQueryResult.data;
  const isLoadingDestination = destinationQueryResult.isLoading;
  const isEditing = !!destinationToEdit; // Evaluates to true if destinationToEdit exists, otherwise evaluates to false

  const form = useDestinationForm(destination, org?.id ?? "", {
    includeInternalFields: true,
    host: env.VITE_APP_API_SERVER,
  });

  // Flag for whether or not we have populated the destination from destinationToEdit
  // Once we set up the initial values, this is set to true so we stop re-populating the destination
  const [destinationPopulated, setDestinationPopulated] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modelsError, setModelsError] = useState<string>();
  const [destinationTestState, setDestinationTestState] = useState<{
    status: string;
  }>({ status: "idle" });

  const setDestinationField = useCallback(
    (key: keyof Destination, value: string | string[] | boolean | number | undefined) => {
      setDestination((currentDestination) => ({
        ...currentDestination,
        [key]: value,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // setDestination is from SDK hook and should be stable, empty deps to prevent re-creation
  );

  const handleOpenDocs = () => setDocsOpen(true);
  const handleCloseDocs = () => setDocsOpen(false);

  useEffect(() => {
    if (form && destinationToEdit && !destinationPopulated) {
      const converted = prepareDestinationFromExisting(destinationToEdit);
      setDestination({ ...converted });
      setDestinationPopulated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, destinationToEdit, destinationPopulated]); // setDestination should be stable from SDK hook

  const selectedRecipient = recipients?.find(({ id }) => id === destination.recipient_id);
  const showVendorFields = isEditing || !!selectedRecipient;

  // Track the last recipient ID we updated products for
  const lastRecipientIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (selectedRecipient && !isEditing && selectedRecipient.id !== lastRecipientIdRef.current) {
      // When we select a recipient for the destination, set the products accordingly
      // Only do this for new destinations, not when editing
      lastRecipientIdRef.current = selectedRecipient.id;
      setDestinationField("products", selectedRecipient.products);
    }
  }, [selectedRecipient, setDestinationField, isEditing]);

  const selectedVendor = (() => {
    if (form) {
      const { fields } = form[0];
      if (fields[0].form_element === "select") {
        return fields[0].enum?.find(({ key }) => key === destination.vendor);
      }
    }
    return undefined;
  })();

  const vendorKey = selectedVendor?.key.toString() || destination.vendor;
  const { data: createDestinationDocs } = useCreateDestinationDocs(vendorKey);

  useEffect(() => {
    if (selectedVendor && !isEditing) {
      // only set defaults when creating new destination
      const defaultAuthMethod = getDefaultAuthMethod(selectedVendor.key.toString());
      const defaultBucketVendor = getDefaultBucketVendor(selectedVendor.key.toString());
      const defaultMetastore = getDefaultMetastore(selectedVendor.key.toString());
      const defaultStorage = getDefaultStorage(selectedVendor.key.toString());
      setDestination((currentDestination) => ({
        ...currentDestination,
        auth_method: defaultAuthMethod,
        bucket_vendor: defaultBucketVendor,
        metastore: defaultMetastore,
        storage: defaultStorage,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor, isEditing]); // setDestination should be stable from SDK hook

  // Track the last storage value we set auth method for
  const lastStorageRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (destination.storage && destination.storage !== lastStorageRef.current) {
      lastStorageRef.current = destination.storage;
      const defaultStorageAuthMethod = getDefaultStorageAuthMethod(destination.storage);
      setDestination((currentDestination) => ({
        ...currentDestination,
        bucket_auth_method: defaultStorageAuthMethod,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination.storage]);

  const selectedVendorDocs = createDestinationDocs;

  const validateForm = () => (formRef.current ? formRef.current.reportValidity() : false);

  const preparedDestination: PreparedDestination = (() => {
    const p = prepareDestinationWithForm(destination, form);
    p.products = destination.id_in_provider_system && !isEditing ? destination.products : undefined;
    return p;
  })();

  // A passing test is required unless the only pending edits are to fields that can't affect connectivity.
  const canSave =
    destinationTestState.status === "success" ||
    (!!destinationToEdit && canSaveWithoutTest(initialFormState(destinationToEdit), destination));

  // Handle destination submission
  const onSave = () => {
    if (destination.enabled_models?.length === 0) {
      setModelsError("This is a required field");
      return;
    }

    if (isEditing && destinationToEdit) {
      const changed = computeChangedFields(destinationToEdit, preparedDestination);
      updateDestinationMutation.mutate(
        {
          destinationID: destinationToEdit.id,
          fields: changed,
          redirectPath: `/destinations/${destinationToEdit.id}`,
        },
        {
          onSuccess: () => {
            navigate(`/destinations/${destinationToEdit.id}`, {
              state: { from: "edit" },
            });
          },
        },
      );
    } else {
      setShowModal(true);
    }
  };

  const onConfirm = () => {
    setShowModal(false);
    createDestinationMutation.mutate(
      {
        destination: preparedDestination,
        redirectPath: "/destinations",
      },
      {
        onSuccess: () => {
          navigate("/destinations");
        },
      },
    );
  };

  // Intercept native form submission, prevent default, and run test
  // We use the default form submission event so that we can borrow the browsers built-in support for handling missing required fields
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave();
  };

  if (!form || (destinationID && isLoadingDestination)) {
    return <Spinner />;
  }

  if (destinationID && !isLoadingDestination && !destinationToEdit) {
    return <Navigate to="/destinations" replace />;
  }

  return (
    <div className="relative h-full">
      <div className="h-full">
        <div className="flex max-w-lg flex-col pb-16">
          {selectedRecipient && (
            <ConfirmDataDestinationModal
              kind="ADD_DESTINATION"
              show={showModal}
              setShow={setShowModal}
              dataIdentifier={selectedRecipient.name}
              dataDestination={extractDataDestination(preparedDestination)}
              onConfirm={onConfirm}
            />
          )}
          <form className="space-y-8" onSubmit={onSubmit} ref={formRef}>
            {form.map((section) => {
              if (!showVendorFields && section.id > RECIPIENT_SECTION_ID) {
                return null;
              }

              if (section.id === ADVANCED_OPTIONS_SECTION_ID) {
                return (
                  <Fragment key={section.id}>
                    <div className="space-y-4">
                      <Models
                        recipientId={destination.recipient_id}
                        destination={destination}
                        setDestinationField={setDestinationField}
                        modelsError={modelsError}
                        disabled={createDestinationMutation.isPending || updateDestinationMutation.isPending}
                        isEditing={isEditing}
                      />
                    </div>
                    <div>
                      <AdvancedOptionsSection
                        fields={section.fields}
                        destination={destination}
                        setDestinationField={setDestinationField}
                        disabled={createDestinationMutation.isPending || updateDestinationMutation.isPending}
                        isEditing={isEditing}
                      />
                    </div>
                  </Fragment>
                );
              }

              return (
                <Fragment key={section.id}>
                  <div key={section.id}>
                    {section.id !== 1 && (
                      <>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          {section.title}
                        </label>
                        {section.subtitle ? (
                          <div className="mt-1 mb-4">
                            <div className="mt-1 text-sm text-gray-500">
                              {section.subtitle}
                              {section.id === 3 && selectedVendor && (
                                <>
                                  {" For assistance, "}
                                  <a
                                    href={selectedVendor.docs}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="
                                      font-medium text-primary-600
                                      hover:text-primary-500
                                    "
                                  >
                                    view our documentation on {selectedVendor.display}.
                                  </a>
                                  <EgressIPsInstructions />
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <br />
                        )}
                      </>
                    )}
                    <div className="space-y-4">
                      {section.fields.map((field) => (
                        <div key={field.name}>
                          <WrapperSelector
                            field={field}
                            connection={destination}
                            setConnectionField={setDestinationField}
                            disabled={createDestinationMutation.isPending || updateDestinationMutation.isPending}
                            isEditing={isEditing}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="h-px w-full bg-gray-200" /> {/* Divider  */}
                  {!showVendorFields && section.id === RECIPIENT_SECTION_ID && (
                    <Alert kind={AlertKind.INFO}>Select a recipient to configure destination connection details.</Alert>
                  )}
                </Fragment>
              );
            })}
            {showVendorFields && (
              <TestDestinationConnection
                key={destinationToEdit?.id ?? "new"}
                beforeSubmitTest={validateForm}
                preparedDestination={preparedDestination}
                existingDestination={destinationToEdit}
                onTestStateChange={setDestinationTestState}
              />
            )}
          </form>
          <div className="mt-8 flex justify-end">
            {isEditing && (
              <Button
                className="mr-3"
                type={ButtonStyle.TERTIARY}
                disabled={createDestinationMutation.isPending || updateDestinationMutation.isPending}
                onClick={() => navigate("/destinations", { replace: true })}
                text="Cancel"
              />
            )}
            <Button
              type={!canSave ? ButtonStyle.TERTIARY : ButtonStyle.PRIMARY}
              disabled={!canSave || createDestinationMutation.isPending || updateDestinationMutation.isPending}
              onClick={onSave}
              text={
                createDestinationMutation.isPending || updateDestinationMutation.isPending ? (
                  <div className="flex">
                    <Spinner.Inline className="mr-3 text-white" />
                    Saving Destination...
                  </div>
                ) : (
                  "Save Destination"
                )
              }
            />
          </div>
        </div>
      </div>
      {selectedVendorDocs?.content && (
        <>
          <Button
            text="Documentation"
            type={ButtonStyle.TERTIARY}
            icon={<DocumentTextIcon className="size-full" />}
            className={`
              fixed top-4 right-4 rounded-sm p-2 text-white transition-opacity duration-300
              ${docsOpen ? "pointer-events-none opacity-0" : "opacity-100"}
            `}
            onClick={handleOpenDocs}
          />
          <div
            className={`
              fixed top-0 right-0 h-full overflow-y-auto border-l transition-transform duration-300 ease-in-out
              ${docsOpen ? "translate-x-0" : "translate-x-full"}
              md:w-1/3
            `}
          >
            <DocumentationSidebar
              markdown={selectedVendorDocs?.content}
              title={selectedVendorDocs?.title || "Setup Guide"}
              onClose={handleCloseDocs}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DestinationForm;
